/**
 * Canonical error codes returned by BFF Route Handlers.
 *
 * Shared between server (route handlers) and client (error maps in wizards).
 * Adding a code here is the ONLY way to introduce a new BFF error — if it's
 * not in this enum, it shouldn't appear in any `{ ok: false, code }` response.
 *
 * Naming: <DOMAIN>_<DESCRIPTION> for specific errors; generic ones are short.
 */
export enum BffErrorCode {
  // ─── Generic ─────────────────────────────────────────────────────────────
  /** Caller has no valid session or token expired. */
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  /** Caller is authenticated but lacks the required persona/role. */
  FORBIDDEN = 'FORBIDDEN',
  /** Request body failed Zod validation. */
  INVALID_INPUT = 'INVALID_INPUT',
  /** Backend returned an error we can't map to a specific code. */
  SERVER_ERROR = 'SERVER_ERROR',
  /** GET /auth/customers/me or /auth/users/me returned null/failed. */
  PROFILE_UNAVAILABLE = "PROFILE_UNAVAILABLE",
  /** The draft changed a profile field the backend has no column for (birth date, gender, e-mail). */
  PROFILE_FIELD_UNSUPPORTED = "PROFILE_FIELD_UNSUPPORTED",

  // ─── Auth / OTP ──────────────────────────────────────────────────────────
  /** OTP code is incorrect. */
  INVALID_CODE = 'INVALID_CODE',
  /** Challenge cookie expired or was cleared. */
  EXPIRED = 'EXPIRED',
  /** Too many failed OTP attempts — challenge invalidated. */
  TOO_MANY_ATTEMPTS = 'TOO_MANY_ATTEMPTS',
  /** Resend requested before minimum interval elapsed. */
  RATE_LIMITED = 'RATE_LIMITED',

  // ─── Register ────────────────────────────────────────────────────────────
  /** Email already in use by another customer. */
  EMAIL_ALREADY_EXISTS = 'EMAIL_ALREADY_EXISTS',
  /** CPF already registered (race condition — validated in prior step). */
  CPF_ALREADY_REGISTERED = 'CPF_ALREADY_REGISTERED',

  // ─── Internal auth ───────────────────────────────────────────────────────
  /** Email or password wrong for backoffice login. */
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  /** User role not in the allowed set for the BFF. */
  UNSUPPORTED_ROLE = 'UNSUPPORTED_ROLE',
  /** New password rejected by the backend's strength requirements (recovery confirm). */
  WEAK_PASSWORD = 'WEAK_PASSWORD',

  // ─── Gift lists ──────────────────────────────────────────────────────────
  /** MVP: one active list per owner. */
  ALREADY_HAS_ACTIVE_LIST = "ALREADY_HAS_ACTIVE_LIST",
  /** A list item that already received gifts cannot be removed or shrunk below what was bought. */
  LIST_ITEM_HAS_PURCHASES = "LIST_ITEM_HAS_PURCHASES",
  /** Handoff refused: the list owner has no e-mail on file, and the approval link goes by e-mail. */
  CUSTOMER_EMAIL_MISSING = "CUSTOMER_EMAIL_MISSING",
  /** Handoff refused: the communication was already sent; sending it again needs `resend: true`. */
  HANDOFF_ALREADY_SENT = "HANDOFF_ALREADY_SENT",

  // ─── Customer addresses ──────────────────────────────────────────────────
  /** The customer's only address cannot be removed (409 from the backend). */
  ADDRESS_LAST_REMAINING = "ADDRESS_LAST_REMAINING",
  /** The primary address cannot be unset without naming another one (409 from the backend). */
  ADDRESS_PRIMARY_REQUIRED = "ADDRESS_PRIMARY_REQUIRED",

  // ─── CEP / Address lookup ────────────────────────────────────────────────
  /** CEP format invalid (not 8 digits). */
  INVALID_CEP = 'INVALID_CEP',
  /** Resource not found (CEP, list, etc.). */
  NOT_FOUND = 'NOT_FOUND',
  /** External service temporarily unavailable (CEP providers down). */
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',

  // ─── Cart / Checkout ─────────────────────────────────────────────────────
  /** A cart holds items from a single gift list; adding from another list conflicts. */
  CART_LIST_CONFLICT = 'CART_LIST_CONFLICT',
  /** Payment was refused by the gateway (declined card, failed charge). */
  PAYMENT_DECLINED = 'PAYMENT_DECLINED',
  /** Payment retry limit reached (max 10 attempts per order). */
  RETRY_LIMIT = 'RETRY_LIMIT',
  /** Public gift-list detail not found for the given id. */
  LIST_NOT_FOUND = 'LIST_NOT_FOUND',
  /** Operation requires a non-empty cart. */
  CART_EMPTY = 'CART_EMPTY',
  /** Backend rejected the item as unavailable/unapproved (422 from the cart service). */
  CART_ITEM_UNAVAILABLE = 'CART_ITEM_UNAVAILABLE',
  /**
   * The quantity asked for is past what the list still has room for — the cap the backend
   * asserts (`assertQuantityAvailable`) and the BFF checks before writing.
   *
   * Distinct from `CART_ITEM_UNAVAILABLE` because the two are different facts and the person
   * acts on them differently: the piece IS available, the quantity is not. Both arrive as 422
   * from the cart service, and routing them to one code told whoever asked for too many that
   * the gift was unavailable — so they removed it.
   */
  CART_ITEM_QTY_EXCEEDED = 'CART_ITEM_QTY_EXCEEDED',
  /** Cart is LOCKED (payment in progress) and can't be edited (409 from the cart service). */
  CART_LOCKED = 'CART_LOCKED',
}
